import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { loginUser, registerUser, getMe } from '../api/auth'

const AuthContext = createContext(null)
const TOKEN_KEY   = 'farafeero_auth_token'

export function AuthProvider({ children }) {
  const [token,     setToken]     = useState(() => localStorage.getItem(TOKEN_KEY))
  const [user,      setUser]      = useState(null)
  const [isLoading, setIsLoading] = useState(Boolean(localStorage.getItem(TOKEN_KEY)))
  const [authModal, setAuthModal] = useState({ open: false, mode: 'login' })

  // Verify stored token on mount
  useEffect(() => {
    if (!token) return
    getMe(token)
      .then(setUser)
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null) })
      .finally(() => setIsLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const data = await loginUser(email, password)
    const tok  = data.token
    localStorage.setItem(TOKEN_KEY, tok)
    setToken(tok)
    const me = await getMe(tok)
    setUser(me)
    return me
  }, [])

  const register = useCallback(async (formData) => {
    await registerUser(formData)
    return login(formData.email, formData.password)
  }, [login])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setToken(null)
    setUser(null)
  }, [])

  const openAuthModal  = (mode = 'login') => setAuthModal({ open: true,  mode })
  const closeAuthModal = ()               => setAuthModal(v => ({ ...v, open: false }))
  const switchMode     = (mode)           => setAuthModal(v => ({ ...v, mode }))

  return (
    <AuthContext.Provider value={{
      user, token, isLoggedIn: Boolean(user), isLoading,
      login, register, logout,
      authModal, openAuthModal, closeAuthModal, switchMode,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
